import {
  Contact,
  CreateContactInput,
  Entity,
  EntityUser,
} from '@admiin-com/ds-graphql';
import {
  WBAutocomplete,
  WBBox,
  WBFlex,
  WBIcon,
  WBLink,
  WBListItemIcon,
  WBTextFieldV2,
  WBTypography,
} from '@admiin-com/ds-web';
import {
  AutocompleteRenderGroupParams,
  CircularProgress,
  MenuItem,
  Paper,
  debounce,
  styled,
  useTheme,
} from '@mui/material';
import React, { useCallback } from 'react';
import { useLookupService } from './useLookupService';
import { useTranslation } from 'react-i18next';
import { ContactCreateModal } from '../../pages/ContactCreateModal/ContactCreateModal';
import { EntityCreateModal } from '../../pages/EntityCreateModal/EntityCreateModal';
import { v4 as uuidv4 } from 'uuid';
import { WBS3Avatar } from '@admiin-com/ds-amplify-web';
import { TextFieldProps } from 'libs/design-system-web/src/lib/components/composites/TextField/TextField';
import { getName } from '../../helpers/contacts';
import AddUserModal from '../AddUserModal/AddUserModal';
import { useModal } from '../../features/components/ModalManager/ModalContext';

export type AutoCompleteDataType =
  | 'Contact'
  | 'Entity'
  | 'EntityUser'
  | 'AutocompleteResults'
  | 'ContactsAndVerifiedEntity'
  | 'Service'
  | 'Signer'
  | 'ClientContacts'
  | 'ClientPartner'
  | 'ClientManager';

export interface AutoCompleteLookupProps extends TextFieldProps {
  value?: any;
  label: string;
  placeholder: string;
  disabled?: boolean;
  onChange: (option: any) => void;
  defaultValue?: any;
  getOptionLabel?: (option: any) => string;
  type: AutoCompleteDataType;
  entityId?: string;
  noPopupIcon?: boolean;
  ocrCreate?: boolean;
  additonalOptions?: any[];
  creatingContact?: CreateContactInput | null;
  clientGroupCode?: string;
  client?: EntityUser;
}

const GroupHeader = styled('div')(({ theme }) => ({
  position: 'sticky',
  margin: '8px 16px',
  fontWeight: 'bold',
  borderBottom: `1px solid ${theme.palette.grey[300]}`,
  paddingTop: theme.spacing(0.51),
  paddingBottom: theme.spacing(1),
}));

const GroupItems = styled('ul')(({ theme }) => ({
  padding: 0,
  marginBottom: theme.spacing(1),
}));

const AutoCompleteLookup = (
  {
    onChange,
    label,
    placeholder,
    disabled = false,
    type,
    client,
    defaultValue,
    noPopupIcon = false,
    value,
    entityId,
    ocrCreate,
    additonalOptions,
    creatingContact,
    clientGroupCode,
    ...props
  }: AutoCompleteLookupProps,
  ref: React.ForwardedRef<HTMLDivElement>
) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [inputValue, setInputValue] = React.useState('');

  const [options, setOptions] = React.useState<readonly any[]>(
    value ? [...(additonalOptions ?? [])] : []
  );

  const { lookup, loading } = useLookupService({
    type,
    entityId,
    clientGroupCode,
  });

  const [modalOpen, setModalOpen] = React.useState<AutoCompleteDataType | null>(
    null
  );
  const handleCloseModal = () => {
    setModalOpen(null);
  };

  const fetch = React.useCallback(
    async (inputValue: string, refetch = false) => {
      if (!lookup) return;
      const results = await lookup(inputValue, refetch);
      // TODO: remove generate label to useLookupservice

      // if (type === 'Entity' && value && getOptionLabel(value) !== inputValue) {
      //   setOptions([value] as any);
      // } else {
      const filteredResults =
        results && Array.isArray(results)
          ? results?.filter((result) => result) ?? []
          : [];

      setOptions([...filteredResults] as any);
      // Keep the dropdown open even if no results
      // setOpen(true);
      // }
    },
    [lookup]
  );
  //TODO: refactor code a bit?
  const fetchDebounced = React.useMemo(() => {
    return debounce(async (inputValue: string, refetch = false) => {
      if (!lookup) return;
      const results = await lookup(inputValue, refetch);
      // TODO: remove generate label to useLookupservice

      // if (type === 'Entity' && value && getOptionLabel(value) !== inputValue) {
      //   setOptions([value] as any);
      // } else {
      const filteredResults =
        results && Array.isArray(results)
          ? results?.filter((result) => result) ?? []
          : [];

      setOptions([...filteredResults] as any);
    }, 150);
  }, [lookup]);

  const [open, setOpen] = React.useState(false);
  const initalLoading = !value && open;

  React.useEffect(() => {
    let active = true;

    if (!initalLoading) {
      return undefined;
    }

    (async () => {
      if (active) {
        fetch('', true);
      }
    })();

    return () => {
      active = false;
    };
  }, [fetch, initalLoading, entityId]);

  const getOptionLabel = React.useCallback((option: any) => {
    return props.getOptionLabel
      ? props.getOptionLabel(option)
      : typeof option === 'string'
      ? option
      : getName(option) ?? '';
  }, []);

  const { openModal } = useModal();

  const fetchFromInputValue = useCallback(
    async (inputValue: string, refetch = false) => {
      console.log('fetch input avlue inputValue', inputValue, refetch);
      if (
        inputValue === getOptionLabel(value) ||
        (value && getOptionLabel(value) === undefined)
      ) {
        await fetchDebounced('', refetch);
      } else await fetchDebounced(inputValue, refetch);
    },
    [getOptionLabel, value, fetchDebounced]
  );

  const handleNewClick = React.useCallback(async () => {
    if (type === 'Entity' && clientGroupCode && client) {
      openModal('linkEntity', {
        clientUser: client,
        onLinkEntity: async (entityUser: EntityUser) => {
          if (entityUser.entity) {
            onChange(entityUser.entity);
          }
          await fetch('', true);
        },
      });
    }
    if (type === 'ClientPartner' || type === 'ClientManager') {
      openModal('settings', {
        tab: 'users',
      });
    } else {
      setModalOpen(type);
    }
  }, [type, clientGroupCode, openModal, client, fetch, onChange]);

  // React.useEffect(() => {
  //   if (!open) {
  //     setOptions([]);
  //   }
  // }, [open]);

  const addNewBox = React.useMemo(
    () =>
      type !== 'AutocompleteResults' ? (
        <WBBox>
          <WBBox p={2} pb={1} pt={0}>
            <WBLink
              variant="body2"
              component={'button'}
              underline="always"
              sx={{ color: theme.palette.primary.main }}
              fontWeight={'bold'}
              onClick={handleNewClick}
            >
              {`${
                type === 'Entity' && clientGroupCode
                  ? t('linkEntity', {
                      ns: 'taskbox',
                      defaultValue: '+ Link client',
                    })
                  : t('addNew', { ns: 'taskbox' })
              } ${t(type, {
                ns: 'taskbox',
              }).toLowerCase()}`}
            </WBLink>
          </WBBox>
        </WBBox>
      ) : (
        <WBBox />
      ),
    [type, theme.palette.primary.main, handleNewClick, clientGroupCode, t]
  );

  const groupCounts: any = {};

  options.forEach((option) => {
    const groupKey = option?.searchType; // Assuming `searchType` defines the group
    groupCounts[groupKey] = (groupCounts[groupKey] || 0) + 1;
  });

  const renderCounter: any = {};

  const renderGroup = (params: AutocompleteRenderGroupParams) => {
    const groupKey = params.group;
    console.log(groupKey);
    renderCounter[groupKey] = (renderCounter[groupKey] || 0) + 1;

    return (
      <li key={params.key}>
        <GroupHeader>{params.group}</GroupHeader>
        {params.group === t('yourContacts', { ns: 'taskbox' }) ? (
          <WBBox sx={{ py: 1 }}>{addNewBox}</WBBox>
        ) : null}
        <GroupItems>{params.children}</GroupItems>
      </li>
    );
  };

  const getGroupLabel = (option: any) => {
    switch (option.searchType) {
      case 'Contacts':
        return t('yourContacts', { ns: 'taskbox' });
      case 'AutoCompleteResults':
        return t('admiinBusinesses', { ns: 'taskbox' });
      case 'Clients':
        return t('Clients', { ns: 'taskbox' });
      default:
        return option.searchType;
    }
  };

  const addedAdditionalOptions = options.concat(
    additonalOptions?.filter(
      (option) => !options.some((o) => o.id === option.id)
    ) ?? []
  );

  const inputPlaceholder =
    type === 'ClientContacts'
      ? t('Search clients and contacts', { ns: 'taskbox' })
      : placeholder;

  const isGrouped =
    type === 'ContactsAndVerifiedEntity' || type === 'ClientContacts';

  React.useEffect(() => {
    fetchFromInputValue(inputValue);
  }, [inputValue, fetchFromInputValue]);

  return (
    <>
      <WBAutocomplete
        fullWidth
        groupBy={isGrouped ? getGroupLabel : undefined}
        renderGroup={renderGroup}
        open={open}
        onOpen={() => {
          setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
        }}
        getOptionLabel={getOptionLabel}
        filterOptions={(options) => {
          return options;
        }}
        filterSelectedOptions={false}
        options={addedAdditionalOptions}
        clearOnEscape
        autoComplete
        // freeSolo
        includeInputInList
        value={value ?? null}
        noOptionsText={
          <WBBox mx={-2}>
            {addNewBox}
            <WBTypography color={'inherit'} mb={1} ml={2}>
              {t(`no${type}`, { ns: 'taskbox' })}
            </WBTypography>
          </WBBox>
        }
        popupIcon={
          noPopupIcon ? null : (
            <WBIcon name="ChevronDown" size={1.3} color={'black'} />
          )
        }
        inputValue={inputValue}
        onChange={(event: React.ChangeEvent<object>, newValue: any) => {
          // setOptions(newValue ? [newValue, ...options] : options);
          onChange(newValue);
        }}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        isOptionEqualToValue={(option: any, value: any) => {
          return option?.id === value?.id;
        }}
        clearIcon={<WBIcon name="Close" color={'black'} size={'small'} />}
        loading={loading}
        loadingText={t(`Searching ${type?.toLowerCase()}`, {
          ns: 'taskbox',
        })}
        renderInput={(params) => (
          <WBTextFieldV2
            label={label}
            ref={ref}
            name={props.name}
            {...params}
            error={props.error}
            helperText={props.helperText}
            placeholder={inputPlaceholder}
            fullWidth
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
        PaperComponent={({ children, ...props }) => (
          <Paper
            {...props}
            sx={{ px: 1, py: 0, boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.1)' }}
            elevation={3}
          >
            {React.Children.map(children, (child) => child)}
          </Paper>
        )}
        renderOption={(props, option, state, ownerState) => {
          const showEmail =
            type === 'ClientPartner' || type === 'ClientManager';
          const optionEmail =
            option?.email ?? option?.invitedEmail ?? option?.user?.email ?? '';

          return (
            <React.Fragment key={option.id ?? uuidv4()}>
              {state.index === 0 &&
              (!isGrouped ||
                (isGrouped && groupCounts['Contacts'] === undefined)) ? (
                <WBBox>{addNewBox}</WBBox>
              ) : null}
              <DenstMenuItem {...props} value={option?.value} disableGutters>
                <WBListItemIcon>
                  <WBS3Avatar
                    sx={{
                      borderRadius: '3px',
                      width: '20px',
                      height: '20px',
                    }}
                    fontSize="body2.fontSize"
                    imgKey={option?.logo?.key}
                    identityId={option?.logo?.identityId}
                    level={option?.logo?.level}
                    companyName={getName(option) ?? ''}
                  />
                </WBListItemIcon>
                <WBFlex flexDirection="column" minWidth={0}>
                  <WBTypography noWrap>{getName(option)}</WBTypography>
                  {showEmail && optionEmail ? (
                    <WBTypography variant="body2" color="text.secondary" noWrap>
                      {optionEmail}
                    </WBTypography>
                  ) : null}
                </WBFlex>
              </DenstMenuItem>
            </React.Fragment>
          );
        }}
        disabled={disabled}
        // freeSolo
      />
      {
        <>
          <ContactCreateModal
            onSuccess={(contact) => {
              onChange(contact);
            }}
            entityId={entityId}
            handleCloseModal={handleCloseModal}
            open={
              modalOpen === 'Contact' ||
              modalOpen === 'ContactsAndVerifiedEntity' ||
              modalOpen === 'ClientContacts'
            }
            ocrCreate={ocrCreate}
            selected={creatingContact}
          />
          <EntityCreateModal
            onSuccess={(entity) => onChange(entity)}
            handleCloseModal={handleCloseModal}
            open={modalOpen === 'Entity'}
          />
          <AddUserModal
            open={modalOpen === 'EntityUser'}
            handleClose={handleCloseModal}
            onSuccess={(user) => onChange(user)}
          />
        </>
      }
    </>
  );
};

export default React.forwardRef(AutoCompleteLookup) as <
  T extends Entity | Contact | null
>(
  props: AutoCompleteLookupProps & React.RefAttributes<HTMLDivElement>
) => React.ReactElement;

const DenstMenuItem = styled(MenuItem)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    '&.MuiMenuItem-root': {
      minHeight: '24px',
      padding: theme.spacing(0.5, 2),
    },
  },
}));
